import React, { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import { TagCheckbox } from './utils';
import { DEPARTURE_KEY_HELP, DEPARTURE_KEY_LABEL } from '../utils/tagCategories';

const CategoryCheckboxes = memo(({
    cat,
    // Trip Defaults only: no tour serializer sends departure_key (utils/tagCategories.js).
    showDepartureKey = false,
    checkinCategoriesCount,
    boardingCategoriesCount,
    onAssociatedCategoryCheckboxChange,
    onCheckinBoardingCheckboxChange
}) => {
    const checkboxConditions = useMemo(() => ({
        showRequired: cat.is_new || (cat.min_tags && cat.min_tags > 0) || cat?.category?.code === 'departure',
        // showRequired: true,
        showGuidesFromTags: cat.is_new || cat.guides_from_tags,
        showChatConversation: cat.is_new || cat.chat_conversation,
        showInChat: cat.is_new || cat.visible_in_chat,
        showLocationSharing: cat.is_new || cat.location_sharing,
        showLocationAllIfNoTag: cat.is_new || cat.location_all_if_no_tag,
        showUserAssociation: cat.is_new || cat.user_association,
        showUserSelected: cat.is_new || cat.user_selected,
        showMaxTags: cat.is_new || cat.max_tags === 1 || cat?.category?.code === 'departure',
        // showMaxTags: true,
        showAllowMultipleTags: cat.is_new || cat.max_tags > 1,
        // showAllowMultipleTags: true,
        showRemoveUponDateChanges: cat.is_new || cat.remove_upon_date_changes,
        showCheckinBoarding: cat.is_new || cat.checkin || cat.boarding,
        showCheckin: !cat.boarding || cat.checkin,
        showBoarding: !cat.checkin || cat.boarding,
        showCleanupOnAssignment: cat.is_new || cat.cleanup_on_assignment,
        showDepartureWide: cat.is_new || cat.departure_wide,
        showDepartureKey: showDepartureKey && (cat.is_new || cat.departure_key),
        showRemoveGuidesOnCleanup: cat.cleanup_on_assignment && (cat.is_new || cat.remove_guides_on_cleanup),
        showRemoveUnreferencedGuides: cat.guides_from_tags && (cat.is_new || cat.remove_unreferenced_guides),
        showCopyTagsToGuides: cat.is_new || cat.copy_tags_to_guides,
        showCheckinDisplay: cat.is_new || cat.checkin_display,
        // Part of the settings identity check (utils/tagCategories.js): a row differing from
        // its operator default only in this flag must be visible as an override (CP-7983).
        showVisibleOnBookingmgmt: cat.is_new || cat.visible_on_bookingmgmt,
        showCopyTagsToSubbookings: true,
    }), [cat, showDepartureKey]);

    return (
        <>
            {checkboxConditions.showRequired && (
                <TagCheckbox
                    field={'required'}
                    label={'This tag category is required'}
                    disabled={cat?.category?.code === 'departure' || !cat.is_new}
                    checked={(cat.min_tags && cat.min_tags > 0) || cat?.category?.code === 'departure'}
                    cat={cat}
                    handleCheckbox={onAssociatedCategoryCheckboxChange}
                />
            )}
            {checkboxConditions.showGuidesFromTags && (
                <TagCheckbox field={'guides_from_tags'} label={'Tags in this category can be linked to guides/staff (location sharing + chat)'} disabled={!cat.is_new} checked={cat.guides_from_tags} cat={cat} handleCheckbox={onAssociatedCategoryCheckboxChange} />
            )}
            {checkboxConditions.showChatConversation && (
                <TagCheckbox field={'chat_conversation'} label={'Tags in this category create new chat conversations'} disabled={!cat.is_new} checked={cat.chat_conversation} cat={cat} handleCheckbox={onAssociatedCategoryCheckboxChange} />
            )}
            {checkboxConditions.showInChat && (
                <TagCheckbox field={'visible_in_chat'} label={'Tags in this category should visually appear in chat'} disabled={!cat.is_new} checked={cat.visible_in_chat} cat={cat} handleCheckbox={onAssociatedCategoryCheckboxChange} />
            )}
            {checkboxConditions.showLocationSharing && (
                <TagCheckbox field={'location_sharing'} label={'Tags in this category determine location sharing groups'} disabled={!cat.is_new} checked={cat.location_sharing} cat={cat} handleCheckbox={onAssociatedCategoryCheckboxChange} />
            )}
            {checkboxConditions.showLocationAllIfNoTag && (
                <TagCheckbox
                    field={'location_all_if_no_tag'}
                    label={"If a user doesn't have a tag from this category, he will see everyone (in this category)"}
                    disabled={!cat.is_new}
                    checked={cat.location_all_if_no_tag}
                    cat={cat}
                    handleCheckbox={onAssociatedCategoryCheckboxChange}
                />
            )}
            {checkboxConditions.showUserAssociation && (
                <TagCheckbox
                    field={'user_association'}
                    label={'Tags in this category can be linked to GPS devices/users (location sharing, without chat)'}
                    disabled={!cat.location_sharing || !cat.is_new}
                    checked={cat.user_association}
                    cat={cat}
                    handleCheckbox={onAssociatedCategoryCheckboxChange}
                />
            )}
            {checkboxConditions.showUserSelected && (
                <TagCheckbox field={'user_selected'} label={'Tags in this category can be self-selected by users on the self-registration page'} disabled={!cat.is_new} checked={cat.user_selected} cat={cat} handleCheckbox={onAssociatedCategoryCheckboxChange} />
            )}
            {checkboxConditions.showMaxTags && (
                <TagCheckbox
                    field={'max_tags'}
                    label={'Only one tag allowed of this category per user, per departure'}
                    disabled={!cat.is_new || cat.max_tags > 1}
                    checked={cat.max_tags === 1 || cat.category?.code === 'departure'}
                    cat={cat}
                    handleCheckbox={onAssociatedCategoryCheckboxChange}
                />
            )}
            {checkboxConditions.showAllowMultipleTags && (
                <TagCheckbox
                    field={'allow_multiple_tags'}
                    label="Allow more than one tag in this category"
                    disabled={!cat.is_new || (cat.category?.code === 'departure') || (cat.max_tags === 1)}
                    checked={cat.max_tags > 1}
                    cat={cat}
                    handleCheckbox={onAssociatedCategoryCheckboxChange}
                />
            )}
            {checkboxConditions.showRemoveUponDateChanges && (
                <TagCheckbox field={'remove_upon_date_changes'} label={'Remove this tag if date changes via XML/integration'} disabled={!cat.is_new} checked={cat.remove_upon_date_changes} cat={cat} handleCheckbox={onAssociatedCategoryCheckboxChange} />
            )}
            {checkboxConditions.showCheckinBoarding && (
                <>
                    {checkboxConditions.showCheckin && (
                        <TagCheckbox
                            field={'checkin'}
                            label={!cat.checkin && cat.boarding
                                ? 'This category is already used for boarding — uncheck Boarding first'
                                : checkinCategoriesCount >= 3 && !cat.checkin
                                    ? 'There are already 3 checkin tag categories selected'
                                    : 'Tags in this category are used for check-in'
                            }
                            disabled={!cat.is_new || (!cat.checkin && (cat.boarding || checkinCategoriesCount >= 3))}
                            checked={cat.checkin}
                            cat={cat}
                            handleCheckbox={(ev) => onCheckinBoardingCheckboxChange(ev, cat.idx, cat.id, 'checkin')}
                        />
                    )}
                    {checkboxConditions.showBoarding && (
                        <TagCheckbox
                            field={'boarding'}
                            label={!cat.boarding && cat.checkin
                                ? 'This category is already used for check-in — uncheck Check-in first'
                                : boardingCategoriesCount >= 1 && !cat.boarding
                                    ? 'There is already 1 boarding tag category selected'
                                    : 'Tags in this category are used for boarding'
                            }
                            disabled={!cat.is_new || (!cat.boarding && (cat.checkin || boardingCategoriesCount >= 1))}
                            checked={cat.boarding}
                            cat={cat}
                            handleCheckbox={(ev) => onCheckinBoardingCheckboxChange(ev, cat.idx, cat.id, 'boarding')}
                        />
                    )}
                </>
            )}
            {checkboxConditions.showCleanupOnAssignment && (
                <TagCheckbox field={'cleanup_on_assignment'} label={'Cleanup on assignment'} disabled={!cat.is_new} checked={cat.cleanup_on_assignment} cat={cat} handleCheckbox={onAssociatedCategoryCheckboxChange} />
            )}
            {checkboxConditions.showDepartureWide && (
                <TagCheckbox field={'departure_wide'} label={'Departure wide assignment'} disabled={!cat.is_new} checked={cat.departure_wide} cat={cat} handleCheckbox={onAssociatedCategoryCheckboxChange} />
            )}
            {checkboxConditions.showDepartureKey && (
                <TagCheckbox field={'departure_key'} label={DEPARTURE_KEY_LABEL} tooltip={DEPARTURE_KEY_HELP} disabled={!cat.is_new} checked={!!cat.departure_key} cat={cat} handleCheckbox={onAssociatedCategoryCheckboxChange} />
            )}
            {checkboxConditions.showRemoveGuidesOnCleanup && (
                <TagCheckbox field={'remove_guides_on_cleanup'} label={'Remove guides on cleanup'} disabled={!cat.is_new} checked={cat.remove_guides_on_cleanup} cat={cat} handleCheckbox={onAssociatedCategoryCheckboxChange} />
            )}
            {checkboxConditions.showRemoveUnreferencedGuides && (
                <TagCheckbox field={'remove_unreferenced_guides'} label={'Remove unreferenced guides'} disabled={!cat.is_new} checked={cat.remove_unreferenced_guides} cat={cat} handleCheckbox={onAssociatedCategoryCheckboxChange} />
            )}
            {checkboxConditions.showCopyTagsToGuides && (
                <TagCheckbox field={'copy_tags_to_guides'} label={'Copy user tags to guides'} disabled={!cat.is_new} checked={cat.copy_tags_to_guides} cat={cat} handleCheckbox={onAssociatedCategoryCheckboxChange} />
            )}
            {checkboxConditions.showCopyTagsToSubbookings && (
                <TagCheckbox field={'copy_tags_to_subbookings'} label={'Copy tags to sub-bookings'} disabled={!cat.is_new} checked={cat.copy_tags_to_subbookings} cat={cat} handleCheckbox={onAssociatedCategoryCheckboxChange} />
            )}
            {checkboxConditions.showCheckinDisplay && (
                <TagCheckbox field={'checkin_display'} label={'Display tags during check-in'} disabled={!cat.is_new} checked={cat.checkin_display} cat={cat} handleCheckbox={onAssociatedCategoryCheckboxChange} />
            )}
            {checkboxConditions.showVisibleOnBookingmgmt && (
                <TagCheckbox field={'visible_on_bookingmgmt'} label={'Visible on booking management page'} disabled={!cat.is_new} checked={cat.visible_on_bookingmgmt} cat={cat} handleCheckbox={onAssociatedCategoryCheckboxChange} />
            )}
        </>
    );
});

CategoryCheckboxes.displayName = 'CategoryCheckboxes';

CategoryCheckboxes.propTypes = {
    cat: PropTypes.shape({
        is_new: PropTypes.bool,
        min_tags: PropTypes.number,
        max_tags: PropTypes.number,
        guides_from_tags: PropTypes.bool,
        chat_conversation: PropTypes.bool,
        visible_in_chat: PropTypes.bool,
        location_sharing: PropTypes.bool,
        location_all_if_no_tag: PropTypes.bool,
        user_association: PropTypes.bool,
        user_selected: PropTypes.bool,
        remove_upon_date_changes: PropTypes.bool,
        checkin: PropTypes.bool,
        boarding: PropTypes.bool,
        cleanup_on_assignment: PropTypes.bool,
        departure_wide: PropTypes.bool,
        departure_key: PropTypes.bool,
        remove_guides_on_cleanup: PropTypes.bool,
        remove_unreferenced_guides: PropTypes.bool,
        copy_tags_to_guides: PropTypes.bool,
        copy_tags_to_subbookings: PropTypes.bool,
        checkin_display: PropTypes.bool,
        visible_on_bookingmgmt: PropTypes.bool,
        idx: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        category: PropTypes.shape({
            code: PropTypes.string,
        }),
    }).isRequired,
    showDepartureKey: PropTypes.bool,
    checkinCategoriesCount: PropTypes.number.isRequired,
    boardingCategoriesCount: PropTypes.number.isRequired,
    onAssociatedCategoryCheckboxChange: PropTypes.func.isRequired,
    onCheckinBoardingCheckboxChange: PropTypes.func.isRequired,
};

export default CategoryCheckboxes;